/**
 * Admin shell — the chrome shared by every system-admin page.
 *
 * Wraps an admin page's content in a two-column layout: a persistent
 * sidebar of admin destinations (Users / LLM / Sign-ups) plus a
 * "back to app" escape hatch, and the page content beside it.
 *
 * The admin pages used to each hang their own link in the top bar; they
 * are now reachable from a single "Admin" entry in the user menu, and
 * navigation between them happens here in the sidebar (per the design
 * direction — group every admin surface behind one nav).
 *
 * The sidebar's active item is passed in as a literal (`active`) — the
 * shell is re-mounted per route by the dispatcher's `branch`, so the
 * highlighted item is fixed for the lifetime of each mount and needs no
 * reactive subscription.
 */
import {
  type Mountable,
  type Renderable,
  type Send,
  type Signal,
  a,
  nav,
  div,
  span,
  text,
} from '@llui/dom';

import { t } from '../i18n.js';
import { appRouteHref, appRouteLink, type AppRoute } from '../router.js';

const navMsg = <Msg>(route: AppRoute): Msg => ({ type: 'navigate', route }) as unknown as Msg;

export type AdminSection = 'users' | 'llm' | 'signups' | 'audit';

interface AdminNavItem {
  section: AdminSection;
  route: AppRoute;
  labelKey: string;
  testid: string;
}

const ITEMS: AdminNavItem[] = [
  { section: 'users', route: { kind: 'admin-users' }, labelKey: 'admin.nav.users', testid: 'admin-nav-users' },
  { section: 'llm', route: { kind: 'admin-llm' }, labelKey: 'admin.nav.llm', testid: 'admin-nav-llm' },
  { section: 'signups', route: { kind: 'admin-signups' }, labelKey: 'admin.nav.signups', testid: 'admin-nav-signups' },
  { section: 'audit', route: { kind: 'admin-audit' }, labelKey: 'admin.nav.audit', testid: 'admin-nav-audit' },
];

export function viewAdminShell<Model, Msg>(
  _state: Signal<Model>,
  send: Send<Msg>,
  active: AdminSection,
  content: Renderable,
): Mountable {
  return div({ class: 'admin-layout', 'data-testid': 'admin-layout' }, [
    nav(
      {
        class: 'admin-sidebar',
        'aria-label': t('admin.nav.label'),
        'data-testid': 'admin-sidebar',
      },
      [
        span({ class: 'admin-sidebar-title' }, [text(t('admin.nav.title'))]),
        div(
          { class: 'admin-sidebar-links' },
          ITEMS.map((item) =>
            appRouteLink(
              send,
              item.route,
              {
                class: active === item.section ? 'admin-sidebar-link is-active' : 'admin-sidebar-link',
                'data-testid': item.testid,
                ...(active === item.section ? { 'aria-current': 'page' } : {}),
              },
              [text(t(item.labelKey))],
              navMsg,
            ),
          ),
        ),
        a(
          {
            class: 'admin-sidebar-back',
            href: appRouteHref({ kind: 'list' }),
            'data-testid': 'admin-sidebar-back',
            onClick: (event: MouseEvent) => {
              event.preventDefault();
              send(navMsg({ kind: 'list' }));
            },
          },
          [text(`← ${t('admin.nav.back')}`)],
        ),
      ],
    ),
    div({ class: 'admin-content' }, content),
  ]);
}
